import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Logo from './logo';
import { getSocialMediaIconFromType } from '../utils/getSocialMediaIconFromType';
import { formatTelHref } from '../utils/phoneUtils';
import { RichText } from 'prismic-reactjs';

const Footer = () => {
  const data = useStaticQuery(graphql`
    query footerQuery {
      allPrismicSocialMedia {
        nodes {
          data {
            social_media_type
            social_media_account_link {
              url
            }
          }
        }
      }
      prismicNavbar {
        data {
          body {
            ... on PrismicNavbarDataBodyMenu {
              primary {
                is_featured
                link_label {
                  raw
                }
                link_url {
                  url
                }
                link_url_anchor
              }
              slice_type
              items {
                link_url {
                  url
                }
                link_label {
                  raw
                }
                link_url_anchor
                description {
                  raw
                }
                icon
              }
            }
          }
        }
      }
      prismicCompanyLogo {
        data {
          company_name
          logo {
            gatsbyImageData(width: 100)
            alt
          }
        }
      }
      prismicFooter {
        data {
          address_1 {
            raw
          }
          email_address {
            url
          }
          email_address_label {
            raw
          }
          copyright_info {
            raw
          }
          address_2 {
            raw
          }
          contact_phone_number {
            raw
          }
        }
      }
    }
  `);

  const {
    prismicCompanyLogo,
    prismicNavbar,
    prismicFooter,
    allPrismicSocialMedia,
  } = data;

  const footerData = prismicFooter?.data;
  const { company_name, logo } = prismicCompanyLogo?.data ?? {};
  const address1Text = footerData?.address_1?.raw?.[0]?.text;
  const address2Text = footerData?.address_2?.raw?.[0]?.text;
  const emailUrl = footerData?.email_address?.url;
  const emailLabel = footerData?.email_address_label?.raw?.[0]?.text;
  const phoneDisplay = footerData?.contact_phone_number?.raw?.[0]?.text;
  const copyrightText = footerData?.copyright_info?.raw?.[0]?.text;
  const menuItems = prismicNavbar?.data?.body ?? [];
  const socialMedias = allPrismicSocialMedia?.nodes ?? [];

  return (
    <footer className='border-t-2 border-primary bg-gray-100 py-8 text-gray-700'>
      <div className='w-full px-6 md:mx-12 md:px-12'>
        <div className='flex flex-col items-center justify-between gap-8 md:flex-row md:items-start md:justify-around'>
          <div className='flex items-center lg:w-auto'>
            {logo && (
              <div className='mr-4 mb-1 w-full self-start pr-1 pb-1'>
                <Logo
                  image={logo.gatsbyImageData}
                  className='max-w-xxxxxs mt-4'
                  alt={logo.alt}
                />
              </div>
            )}
            <address className='mt-4 flex flex-col text-left text-sm not-italic'>
              {company_name && (
                <p className='py-1 text-lg font-semibold'>{company_name}</p>
              )}
              {address1Text && <p className='py-1'>{address1Text}</p>}
              {address2Text && <p className='py-1'>{address2Text}</p>}
              {emailUrl && (
                <a
                  href={emailUrl}
                  className='py-1 text-primary'
                  aria-label={emailLabel ? `Email us at ${emailLabel}` : 'Email us'}
                >
                  {emailLabel ?? 'Email us'}
                </a>
              )}
              {phoneDisplay && (
                <a
                  href={formatTelHref(phoneDisplay)}
                  className='py-1 text-primary'
                  aria-label={`Call us at ${phoneDisplay}`}
                >
                  {phoneDisplay}
                </a>
              )}
              <div className='flex'>
                {socialMedias.map((sm, index) => {
                  const linkUrl = sm?.data?.social_media_account_link?.url;
                  if (!linkUrl) {
                    return null;
                  }
                  return (
                    <div key={index} className='mr-2 mt-2'>
                      <a
                        href={linkUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={`Visit our ${sm.data.social_media_type} page`}
                      >
                        <FontAwesomeIcon
                          icon={getSocialMediaIconFromType(
                            sm.data.social_media_type
                          )}
                          size='2x'
                          aria-hidden
                        />
                      </a>
                    </div>
                  );
                })}
              </div>
            </address>
          </div>
          <div className='flex w-full items-center justify-between font-medium text-primary md:w-4/12'>
            <div className='flex flex-col flex-wrap justify-center gap-1'>
              {menuItems
                .filter(
                  (m) => RichText.asText(m.primary.link_label.raw) !== 'More'
                )
                .map((menuItem, index) => {
                  const { link_label, link_url, link_url_anchor } =
                    menuItem.primary;

                  let subItemsContent = null;

                  if (menuItem.items.length > 0) {
                    subItemsContent = menuItem.items.map((item, index2) => {
                      return (
                        <Link
                          key={index2}
                          className='p-1 text-xs hover:font-medium hover:text-primary hover:underline md:text-sm'
                          to={`${item.link_url.url}${
                            item.link_url_anchor
                              ? '#' + item.link_url_anchor
                              : ''
                          }`}
                        >
                          <span>{item.link_label.raw[0].text}</span>
                        </Link>
                      );
                    });
                  }

                  return (
                    <>
                      <Link
                        key={index}
                        className='p-1 text-xs hover:font-medium hover:text-primary hover:underline md:text-sm'
                        to={`${link_url.url}${
                          link_url_anchor ? '#' + link_url_anchor : ''
                        }`}
                      >
                        <span>{link_label.raw[0].text}</span>
                      </Link>
                      {subItemsContent}
                    </>
                  );
                })}
            </div>
          </div>
        </div>
        {copyrightText && (
          <div className='text-center text-xs'>
            <p>
              {copyrightText}&copy; {new Date().getFullYear()}
            </p>
          </div>
        )}
        <div className='text-center text-xs'>
          <p>
            Website created by{' '}
            <a
              href='https://redbrickroadstudio.com'
              rel='noopener noreferrer'
              target='_blank'
            >
              Red Brick Road Studio
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
